/**
 * Demo overlay: bridges the real driver roster to the prototype's net-new
 * concepts.
 *
 * The dispatcher views the prototype with a real account, so the live roster
 * supplies the set of memberships and their feed ids. Everything shown on top —
 * a seeded demo name plus the freight/task context that doesn't exist yet (AS400
 * status codes, PTA, appointment + HOS risk, route traffic, suggested tasks) —
 * is layered on deterministically from the static demo templates so every driver
 * gets a stable, plausible, fully-seeded identity instead of DB-sourced names.
 */

import { assignedByFor } from "../assignments";
import { FIRST_NAMES, LAST_NAMES } from "../boards";
import { DRIVERS, TASKS } from "../data";
import type { DispatchTask, Driver } from "../types";

/** Minimal slice of the live `DriverRow` the overlay needs. */
export interface RealDriverInput {
  workspaceMembershipId: string;
  name: string | null;
  alias: string | null;
  feedId: string | null;
  accountId: string | null;
  location?: string | null;
}

/** Stable non-negative hash so a given membership always maps to the same template. */
function hash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Prefer a real numeric alias as the truck number; otherwise derive a stable 4-digit one. */
function deriveTruck(row: RealDriverInput): string {
  const aliasDigits = row.alias?.match(/\d{3,5}/)?.[0];
  if (aliasDigits) return aliasDigits;
  return String(1000 + (hash(row.workspaceMembershipId) % 9000));
}

/**
 * Deterministic seeded demo name for a membership, drawn from the same pools the
 * Live Feed uses so the prototype shows one consistent fake fleet instead of the
 * DB-sourced roster names.
 */
export function seededDriverName(workspaceMembershipId: string): string {
  const first = FIRST_NAMES[hash(`firstname::${workspaceMembershipId}`) % FIRST_NAMES.length];
  const last = LAST_NAMES[hash(`lastname::${workspaceMembershipId}`) % LAST_NAMES.length];
  return `${first} ${last}`;
}

/**
 * Overlay the demo freight context onto the real roster.
 *
 * @param rows - Live driver rows (subset of `DriverRow`).
 * @returns One `Driver` per real row; empty roster falls back to static demo drivers.
 */
export function buildDemoDrivers(rows: RealDriverInput[]): Driver[] {
  if (rows.length === 0) return DRIVERS;

  return rows.map((row, i) => {
    const template = DRIVERS[hash(row.workspaceMembershipId + i) % DRIVERS.length];
    return {
      ...template,
      id: row.workspaceMembershipId,
      feedId: row.feedId,
      accountId: row.accountId,
      name: seededDriverName(row.workspaceMembershipId),
      truck: deriveTruck(row),
    };
  });
}

/**
 * Generate demo tasks against real drivers, preserving the four task states.
 *
 * @param drivers - Overlaid drivers (from {@link buildDemoDrivers}).
 * @returns One task per demo template, each bound to a distinct real driver.
 */
export function buildDemoTasks(drivers: Driver[]): DispatchTask[] {
  if (drivers.length === 0) return TASKS;

  return TASKS.map((template, i) => {
    const driver = drivers[i % drivers.length];
    return {
      ...template,
      id: `${driver.id}:${template.type}`,
      driverId: driver.id,
      assignedBy: template.assignedToMe ? assignedByFor(driver.id) : template.assignedBy,
    };
  });
}
